/**
 * WhyChooseUsBuilder — interactive wizard + card library for the
 * tenant Why-Choose-Us section.
 *
 * Flow (smart):
 *   - If the tenant already has a generated library (8 cards from a prior
 *     wizard run), the dialog opens directly on the library view. Owner
 *     can re-pick / edit any card without re-generating. They can hit
 *     "Tweak answers" to revisit the wizard with prior answers prefilled.
 *   - First-time owners see the 4-step wizard, then the library at step 5.
 *
 * Persistence:
 *   - draft.why_choose_us         → 4-6 currently published cards
 *   - draft.why_choose_us_library → all 8 generated candidates (the pool)
 *   - draft.why_choose_us_answers → last wizard answers (for prefill)
 *
 * Selected cards remain editable forever — even after publish.
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    Sparkles, Loader2, ChevronLeft, ChevronRight, Check, Edit3,
    Wand2, MessageSquareText, Award, Bike, Users, Calendar,
    Bell, Gift, Activity, Camera, Receipt, ShieldCheck, Truck,
    Coffee, Heart, Zap, Phone, RefreshCw, AlertCircle
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { useToast } from '../../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Owner-facing feature catalog. The keys are sent to the LLM so it can
// reference each one as a real feature instead of inventing things.
// Mix of (a) garage-side things the owner controls and (b) Motopotti
// platform features that benefit customers — both are equally valid
// "why choose us" cards.
const FEATURES = [
    // Motopotti platform features (customer-facing)
    { key: 'job_tracking', icon: Activity, en: 'Live job tracking', bn: 'লাইভ জব ট্র্যাকিং', hint: 'Customers see status from drop-off to delivery' },
    { key: 'photo_updates', icon: Camera, en: 'Photo updates', bn: 'ছবি আপডেট', hint: 'Pictures of the work shared at every step' },
    { key: 'sms_reminders', icon: Bell, en: 'Auto service reminders', bn: 'অটো সার্ভিস রিমাইন্ডার', hint: 'Next oil change / service SMS automatically' },
    { key: 'digital_invoice', icon: Receipt, en: 'Digital invoice', bn: 'ডিজিটাল ইনভয়েস', hint: 'Itemised bill on WhatsApp — no paper' },
    { key: 'loyalty', icon: Gift, en: 'Loyalty / points', bn: 'লয়্যালটি পয়েন্ট', hint: 'Repeat-customer discounts or rewards' },
    { key: 'referral', icon: Heart, en: 'Referral bonus', bn: 'রেফার বোনাস', hint: 'Bring a friend, both get a discount' },
    // Garage-side promises
    { key: 'genuine_parts', icon: ShieldCheck, en: 'Genuine parts only', bn: 'অরিজিনাল পার্টস', hint: 'Box shown before fitting, bill kept' },
    { key: 'service_warranty', icon: Award, en: 'Service warranty', bn: 'সার্ভিস ওয়ারেন্টি', hint: 'We re-do the job if the issue returns' },
    { key: 'pickup_drop', icon: Truck, en: 'Pickup & drop', bn: 'পিকআপ ও ড্রপ', hint: 'Free within city limits' },
    { key: 'express_lane', icon: Zap, en: 'Express service', bn: 'এক্সপ্রেস সার্ভিস', hint: 'Quick jobs done in under an hour' },
    { key: 'female_friendly', icon: Heart, en: 'Female-friendly', bn: 'নারীবান্ধব', hint: 'Safe, respectful service for all' },
    { key: 'lounge', icon: Coffee, en: 'Customer lounge', bn: 'কাস্টমার লাউঞ্জ', hint: 'Wait in comfort, free WiFi' },
    { key: 'emergency_24x7', icon: Phone, en: '24/7 emergency', bn: '২৪/৭ ইমার্জেন্সি', hint: 'On-call roadside help' },
    { key: 'certified_team', icon: Users, en: 'Certified mechanics', bn: 'প্রশিক্ষিত মেকানিক', hint: 'Manufacturer-trained technicians' }
];

const TONES = [
    { id: 'bold', label: 'Bold', desc: 'Confident & direct' },
    { id: 'professional', label: 'Professional', desc: 'Clear & polished' },
    { id: 'friendly', label: 'Friendly', desc: 'Warm & approachable' }
];

const TYPES = [
    { id: 'general', label: 'General service' },
    { id: 'specialist', label: 'Specialist' },
    { id: 'premium', label: 'Premium' }
];

const BN_EMPHASIS = [
    { id: 'high', label: 'Heavy Bengali', desc: 'Bilingual customers' },
    { id: 'medium', label: 'Balanced', desc: 'Mix of both' },
    { id: 'low', label: 'English-first', desc: 'Mostly English' }
];

const DAILY_JOBS = ['<10', '10-20', '20-50', '50+'];

export default function WhyChooseUsBuilder({
    tenant,
    selected = [],          // current draft.why_choose_us
    library: libraryProp = null, // current draft.why_choose_us_library (all 8)
    answers: savedAnswers = null, // last wizard answers
    onApply,                // (cardsArray) => void
    formatApiError = err => err?.message || 'Something went wrong'
}) {
    const { toast } = useToast();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [library, setLibrary] = useState(null); // 8 generated cards
    const [chosen, setChosen] = useState(new Set()); // indexes within library
    const [editing, setEditing] = useState(null); // index being edited

    // Wizard answers
    const [answers, setAnswers] = useState({
        type: 'general',
        years_in_business: '',
        team_size: '',
        daily_jobs: '20-50',
        bilingual_audience: true,
        specializes: '',
        features: {
            job_tracking: true,
            photo_updates: true,
            digital_invoice: true,
            genuine_parts: true,
            service_warranty: true
        },
        standout_points: ['', '', ''],
        tone: 'bold',
        bn_emphasis: 'high',
        extra_context: ''
    });

    // Open behaviour:
    //   - If we already have a library + chosen selection, jump straight to
    //     step 5 so the owner can edit/swap without re-running the wizard.
    //   - Else show step 1.
    useEffect(() => {
        if (!open) return;
        // Prefill from saved wizard answers
        if (savedAnswers && typeof savedAnswers === 'object') {
            setAnswers(a => ({
                ...a,
                type: savedAnswers.type || a.type,
                years_in_business: savedAnswers.years_in_business ?? a.years_in_business ?? '',
                team_size: savedAnswers.team_size ?? a.team_size ?? '',
                daily_jobs: savedAnswers.daily_jobs || a.daily_jobs,
                bilingual_audience: savedAnswers.bilingual_audience !== undefined
                    ? !!savedAnswers.bilingual_audience : a.bilingual_audience,
                specializes: Array.isArray(savedAnswers.specializes)
                    ? savedAnswers.specializes.join(', ')
                    : (savedAnswers.specializes || a.specializes),
                features: { ...a.features, ...(savedAnswers.features || {}) },
                standout_points: [
                    savedAnswers.standout_points?.[0] || '',
                    savedAnswers.standout_points?.[1] || '',
                    savedAnswers.standout_points?.[2] || ''
                ],
                tone: savedAnswers.tone || a.tone,
                bn_emphasis: savedAnswers.bn_emphasis || a.bn_emphasis,
                extra_context: savedAnswers.extra_context || ''
            }));
        } else {
            setAnswers(a => ({
                ...a,
                type: tenant?.type || a.type,
                specializes: Array.isArray(tenant?.specializations)
                    ? tenant.specializations.join(', ')
                    : a.specializes
            }));
        }

        // Hydrate library + chosen from props.
        if (Array.isArray(libraryProp) && libraryProp.length > 0) {
            setLibrary(libraryProp);
            // Pre-check the cards whose title matches the currently-selected list.
            const selTitles = new Set((selected || []).map(c => (c?.title || '').trim()));
            const idxs = libraryProp
                .map((c, i) => (selTitles.has((c?.title || '').trim()) ? i : -1))
                .filter(i => i >= 0);
            setChosen(new Set(idxs.length ? idxs : libraryProp.slice(0, 6).map((_, i) => i)));
            setStep(5);
        } else {
            setLibrary(null);
            setChosen(new Set());
            setStep(1);
        }
    }, [open, libraryProp, savedAnswers, selected, tenant]);

    function toggleFeature(key) {
        setAnswers(a => ({
            ...a,
            features: { ...(a.features || {}), [key]: !a.features?.[key] }
        }));
    }
    function setStandout(i, v) {
        setAnswers(a => {
            const arr = [...a.standout_points];
            arr[i] = v;
            return { ...a, standout_points: arr };
        });
    }

    async function generate() {
        setLoading(true);
        try {
            const specsList = (answers.specializes || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
            const payload = {
                name: tenant?.name || 'Garage',
                name_bn: tenant?.name_bn || undefined,
                type: answers.type || 'general',
                specializes: specsList.length ? specsList : undefined,
                tone: answers.tone || 'bold',
                bn_emphasis: answers.bn_emphasis || 'high',
                years_in_business: answers.years_in_business
                    ? Number(answers.years_in_business) : undefined,
                team_size: answers.team_size
                    ? Number(answers.team_size) : undefined,
                daily_jobs: answers.daily_jobs || undefined,
                bilingual_audience: !!answers.bilingual_audience,
                features: answers.features || {},
                standout_points: (answers.standout_points || []).filter(s => s && s.trim()),
                extra_context: (answers.extra_context || '').trim() || undefined
            };
            const res = await axios.post(`${API}/settings/why-choose-us/generate`, payload);
            const items = res.data?.items || [];
            setLibrary(items);
            // Pre-select the first 6 by default — owner can adjust.
            setChosen(new Set(items.slice(0, 6).map((_, i) => i)));
            setStep(5);
            toast({ title: 'Generated', description: `${items.length} card ideas ready. Pick the ones you love.` });
        } catch (err) {
            toast({
                title: 'Generation failed',
                description: formatApiError(err),
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    }

    function toggleChosen(i) {
        setChosen(s => {
            const next = new Set(s);
            if (next.has(i)) next.delete(i);
            else if (next.size >= 6) return next; // cap at 6
            else next.add(i);
            return next;
        });
    }

    function patchLibraryCard(i, patch) {
        setLibrary(arr => {
            const copy = [...arr];
            copy[i] = { ...copy[i], ...patch };
            return copy;
        });
    }

    function apply() {
        if (!library) return;
        const cards = Array.from(chosen)
            .sort((a, b) => a - b)
            .map(i => library[i])
            .filter(Boolean);
        if (cards.length < 4) {
            toast({
                title: 'Pick at least 4 cards',
                description: 'A great Why-Choose-Us section needs 4–6 promises.',
                variant: 'destructive'
            });
            return;
        }
        // Pass back BOTH the published list and the full library so the
        // parent can persist them. The library survives so the owner can
        // come back later and re-pick / re-edit without re-generating.
        onApply?.(cards, { library, answers: snapshotAnswers(answers) });
        setOpen(false);
        // reset for next open
        setStep(1);
        setEditing(null);
        toast({ title: 'Applied', description: `${cards.length} cards saved to your draft.` });
    }

    function reset() {
        setStep(1);
        setLibrary(null);
        setChosen(new Set());
        setEditing(null);
    }

    const hasExisting = Array.isArray(selected) && selected.length > 0;
    const hasLibrary = Array.isArray(libraryProp) && libraryProp.length > 0;

    return (
        <>
            <Button onClick={() => setOpen(true)} variant="outline" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
                {hasLibrary
                    ? 'Edit cards'
                    : hasExisting ? 'Open wizard' : 'Build with wizard'}
            </Button>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            {step <= 4 ? 'Tell us about your garage' : 'Pick your cards'}
                        </DialogTitle>
                        <DialogDescription>
                            {step <= 4
                                ? 'Answer a few quick questions. Our wizard turns them into customer-facing promises.'
                                : 'Toggle the cards you want on your site (4–6) and edit any text. Your library is saved — no need to regenerate.'}
                        </DialogDescription>
                    </DialogHeader>

                    {step <= 4 && <WizardStepper step={step} />}

                    <div className="mt-4">
                        {step === 1 && <Step1 answers={answers} setAnswers={setAnswers} />}
                        {step === 2 && <Step2 answers={answers} toggleFeature={toggleFeature} />}
                        {step === 3 && <Step3 answers={answers} setStandout={setStandout} setAnswers={setAnswers} />}
                        {step === 4 && <Step4 answers={answers} setAnswers={setAnswers} />}
                        {step === 5 && library && (
                            <Step5Library
                                library={library}
                                chosen={chosen}
                                editing={editing}
                                setEditing={setEditing}
                                toggleChosen={toggleChosen}
                                patch={patchLibraryCard}
                            />
                        )}
                    </div>

                    <DialogFooter className="gap-2 sm:gap-2 mt-4 flex-wrap">
                        {step === 5 ? (
                            <>
                                <Button variant="outline" onClick={() => setStep(1)} disabled={loading}>
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Tweak answers
                                </Button>
                                <Button variant="outline" onClick={generate} disabled={loading}>
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                                    Re-generate
                                </Button>
                                <Button onClick={apply} disabled={loading || chosen.size < 4} className="bg-emerald-600 hover:bg-emerald-700">
                                    <Check className="w-4 h-4 mr-1.5" />
                                    Use {chosen.size} card{chosen.size === 1 ? '' : 's'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>
                                    Cancel
                                </Button>
                                {hasLibrary && (
                                    <Button variant="outline" onClick={() => setStep(5)} disabled={loading}>
                                        Skip to library
                                    </Button>
                                )}
                                {step > 1 && (
                                    <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={loading}>
                                        <ChevronLeft className="w-4 h-4 mr-1" /> Back
                                    </Button>
                                )}
                                {step < 4 ? (
                                    <Button onClick={() => setStep(s => s + 1)}>
                                        Next <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button onClick={generate} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                                        Generate cards
                                    </Button>
                                )}
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

// Snapshot the current wizard form into the shape the backend persists
// under tenant.why_choose_us_answers.
function snapshotAnswers(a) {
    if (!a) return null;
    const specs = (a.specializes || '')
        .split(',').map(s => s.trim()).filter(Boolean);
    return {
        type: a.type,
        specializes: specs,
        tone: a.tone,
        bn_emphasis: a.bn_emphasis,
        years_in_business: a.years_in_business ? Number(a.years_in_business) : null,
        team_size: a.team_size ? Number(a.team_size) : null,
        daily_jobs: a.daily_jobs,
        bilingual_audience: !!a.bilingual_audience,
        features: a.features || {},
        standout_points: (a.standout_points || []).filter(s => s && s.trim()),
        extra_context: a.extra_context || ''
    };
}

function WizardStepper({ step }) {
    const labels = ['Basics', 'Features', 'Standouts', 'Tone'];
    return (
        <div className="flex items-center gap-2 text-xs">
            {labels.map((l, i) => {
                const n = i + 1;
                const active = n === step;
                const done = n < step;
                return (
                    <React.Fragment key={l}>
                        <div className={[
                            'flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition',
                            active ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                                : done ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-slate-50 text-slate-500 border-slate-200'
                        ].join(' ')}>
                            <span className={[
                                'w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold',
                                active ? 'bg-white text-blue-600'
                                    : done ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-white'
                            ].join(' ')}>{done ? <Check className="w-2.5 h-2.5" /> : n}</span>
                            {l}
                        </div>
                        {i < labels.length - 1 && <div className="h-px flex-1 bg-slate-200" />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

// ---------- Step 1 ----------
function Step1({ answers, setAnswers }) {
    return (
        <div className="space-y-4">
            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">What kind of garage are you?</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {TYPES.map(o => (
                        <Pill
                            key={o.id}
                            active={answers.type === o.id}
                            onClick={() => setAnswers(a => ({ ...a, type: o.id }))}
                        >
                            {o.label}
                        </Pill>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Years in business</Label>
                    <Input
                        type="number" min="0" max="100"
                        placeholder="e.g. 8"
                        value={answers.years_in_business}
                        onChange={e => setAnswers(a => ({ ...a, years_in_business: e.target.value }))}
                        className="mt-1.5"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Team size</Label>
                    <Input
                        type="number" min="1" max="200"
                        placeholder="e.g. 5"
                        value={answers.team_size}
                        onChange={e => setAnswers(a => ({ ...a, team_size: e.target.value }))}
                        className="mt-1.5"
                    />
                </div>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Jobs per day (typical)</Label>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                    {DAILY_JOBS.map(o => (
                        <Pill
                            key={o}
                            active={answers.daily_jobs === o}
                            onClick={() => setAnswers(a => ({ ...a, daily_jobs: o }))}
                        >
                            {o}
                        </Pill>
                    ))}
                </div>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Specializes in (comma separated, optional)</Label>
                <Input
                    placeholder="Yamaha R15, Honda CBR, Suzuki Gixxer"
                    value={answers.specializes}
                    onChange={e => setAnswers(a => ({ ...a, specializes: e.target.value }))}
                    className="mt-1.5"
                />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
                <div>
                    <div className="text-sm font-medium text-slate-900">Mostly bilingual customers?</div>
                    <div className="text-xs text-slate-500">If yes, the wizard will lean on Bengali phrases too.</div>
                </div>
                <Switch
                    checked={!!answers.bilingual_audience}
                    onCheckedChange={v => setAnswers(a => ({ ...a, bilingual_audience: v }))}
                />
            </div>
        </div>
    );
}

// ---------- Step 2 — features ----------
function Step2({ answers, toggleFeature }) {
    return (
        <div className="space-y-3">
            <div className="text-sm text-slate-600">
                Tick everything you actually offer. The wizard will only mention enabled features —
                so customers see real promises, not invented ones.
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
                {FEATURES.map(f => {
                    const Icon = f.icon;
                    const on = !!answers.features?.[f.key];
                    return (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => toggleFeature(f.key)}
                            className={[
                                'flex items-start gap-3 p-3 rounded-lg border text-left transition',
                                on ? 'border-blue-500 bg-blue-50/60'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                            ].join(' ')}
                        >
                            <div className={[
                                'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                                on ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                            ].join(' ')}>
                                <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                                    {f.en}
                                    {on && <Check className="w-3.5 h-3.5 text-blue-600" />}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">{f.hint}</div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---------- Step 3 — standouts ----------
function Step3({ answers, setStandout, setAnswers }) {
    return (
        <div className="space-y-3">
            <div className="text-sm text-slate-600">
                Up to 3 things that make your garage stand out. One short line each — like
                <span className="italic"> "trained at Yamaha factory"</span> or <span className="italic">"only AC waiting room in the area"</span>.
            </div>
            {[0, 1, 2].map(i => (
                <Input
                    key={i}
                    placeholder={`Standout #${i + 1}`}
                    value={answers.standout_points[i] || ''}
                    onChange={e => setStandout(i, e.target.value)}
                    maxLength={80}
                />
            ))}

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mt-2 block">Anything else worth mentioning? (optional)</Label>
                <Textarea
                    rows={3}
                    placeholder="Awards, signature service, unique tools…"
                    value={answers.extra_context}
                    onChange={e => setAnswers(a => ({ ...a, extra_context: e.target.value }))}
                    className="mt-1.5"
                />
            </div>
        </div>
    );
}

// ---------- Step 4 — tone ----------
function Step4({ answers, setAnswers }) {
    return (
        <div className="space-y-4">
            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Tone of voice</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {TONES.map(o => (
                        <Pill
                            key={o.id}
                            active={answers.tone === o.id}
                            onClick={() => setAnswers(a => ({ ...a, tone: o.id }))}
                            stack
                        >
                            <span className="font-semibold">{o.label}</span>
                            <span className="text-[10px] opacity-70">{o.desc}</span>
                        </Pill>
                    ))}
                </div>
            </div>

            <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Bengali emphasis</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                    {BN_EMPHASIS.map(o => (
                        <Pill
                            key={o.id}
                            active={answers.bn_emphasis === o.id}
                            onClick={() => setAnswers(a => ({ ...a, bn_emphasis: o.id }))}
                            stack
                        >
                            <span className="font-semibold">{o.label}</span>
                            <span className="text-[10px] opacity-70">{o.desc}</span>
                        </Pill>
                    ))}
                </div>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                    The wizard will generate <strong>8 cards</strong>. You'll pick 4–6 to publish on
                    your site, edit any text, and come back later — your library is saved.
                </span>
            </div>
        </div>
    );
}

// ---------- Step 5 — library ----------
function Step5Library({ library, chosen, editing, setEditing, toggleChosen, patch }) {
    return (
        <div className="space-y-3">
            <div className="text-xs text-slate-500">
                {chosen.size}/6 selected · click a card to {chosen.size >= 6 ? 'deselect' : 'toggle'}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                {library.map((c, i) => {
                    const on = chosen.has(i);
                    const isEditing = editing === i;
                    return (
                        <div
                            key={i}
                            className={[
                                'relative rounded-lg border p-3 transition',
                                on ? 'border-blue-500 bg-blue-50/40 ring-1 ring-blue-500/30'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                            ].join(' ')}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <button
                                    type="button"
                                    onClick={() => toggleChosen(i)}
                                    className={[
                                        'w-6 h-6 rounded-full border flex items-center justify-center shrink-0 transition',
                                        on ? 'bg-blue-600 border-blue-600 text-white'
                                            : 'border-slate-300 text-transparent hover:border-blue-400'
                                    ].join(' ')}
                                    title={on ? 'Selected' : 'Select'}
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditing(isEditing ? null : i)}
                                    className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                                >
                                    <Edit3 className="w-3 h-3" />
                                    {isEditing ? 'Done' : 'Edit'}
                                </button>
                            </div>

                            {isEditing ? (
                                <div className="mt-2 space-y-2">
                                    <Input
                                        value={c.title || ''}
                                        onChange={e => patch(i, { title: e.target.value })}
                                        placeholder="Title"
                                        className="text-sm"
                                    />
                                    <Input
                                        value={c.title_bn || ''}
                                        onChange={e => patch(i, { title_bn: e.target.value })}
                                        placeholder="Title (Bengali)"
                                        className="text-sm font-bn"
                                    />
                                    <Textarea
                                        rows={2}
                                        value={c.body || ''}
                                        onChange={e => patch(i, { body: e.target.value })}
                                        placeholder="Body"
                                        className="text-xs"
                                    />
                                    <Textarea
                                        rows={2}
                                        value={c.body_bn || ''}
                                        onChange={e => patch(i, { body_bn: e.target.value })}
                                        placeholder="Body (Bengali)"
                                        className="text-xs font-bn"
                                    />
                                </div>
                            ) : (
                                <div className="mt-2">
                                    <div className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">
                                        {c.icon}
                                    </div>
                                    <div className="font-semibold text-sm text-slate-900 mt-0.5">{c.title}</div>
                                    {c.title_bn && <div className="text-xs text-slate-700 font-bn">{c.title_bn}</div>}
                                    <div className="text-xs text-slate-600 mt-1.5 line-clamp-3">{c.body}</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {chosen.size > 6 && (
                <div className="text-xs text-red-600 inline-flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" /> Maximum 6 cards.
                </div>
            )}
        </div>
    );
}

// ---------- atoms ----------
function Pill({ active, onClick, children, stack = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                'px-3 py-2 rounded-lg border text-sm transition',
                stack ? 'flex flex-col items-start text-left gap-0.5' : '',
                active
                    ? 'border-blue-600 bg-blue-50 text-blue-700 font-semibold'
                    : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
            ].join(' ')}
        >
            {children}
        </button>
    );
}
